import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Phone, Mail } from 'lucide-react';
import { Button } from './button';
import * as Dialog from '@radix-ui/react-dialog';
import { reportPhoneConversion } from '@/utils/analytics';

export function ExitIntentPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user has already seen the popup
    const hasSeenPopup = sessionStorage.getItem('exitIntentShown');
    if (hasSeenPopup) return;

    // Track mouse leaving viewport
    const handleMouseLeave = (e: MouseEvent) => {
      // Only trigger if mouse is moving upward (leaving the page)
      if (e.clientY <= 0) {
        setIsOpen(true);
        sessionStorage.setItem('exitIntentShown', 'true');
        document.removeEventListener('mouseleave', handleMouseLeave);
      }
    };

    // Add event listener after visitors have had time to read the page.
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 12000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content 
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-8 shadow-2xl"
          aria-describedby="exit-intent-description"
        >
          <Dialog.Close asChild>
            <button
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#e69138] focus:ring-offset-2 rounded"
              aria-label="Close popup"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </Dialog.Close>

          <div className="text-center">
            <Dialog.Title className="text-2xl font-bold text-gray-900">
              Need a quote or service visit?
            </Dialog.Title>
            <p id="exit-intent-description" className="mt-2 text-gray-600">
              Tell us what system you are working on and we will route it to the right fire alarm, monitoring, access, CCTV, burglar alarm, or ERRCS team.
            </p>

            <div className="mt-6 space-y-3">
              <Button
                className="w-full bg-[#e69138] hover:bg-[#d17f2a] text-white"
                onClick={() => {
                  setIsOpen(false);
                  reportPhoneConversion();
                }}
                aria-label="Call (800) 540-2140"
              >
                <Phone className="h-4 w-4 mr-2" aria-hidden="true" />
                Call (800) 540-2140
              </Button>
              <Button
                variant="outline"
                className="w-full border-[#e69138] text-[#e69138] hover:bg-[#e69138]/10"
                onClick={() => {
                  navigate('/contact');
                  setIsOpen(false);
                }}
                aria-label="Request a quote"
              >
                <Mail className="h-4 w-4 mr-2" aria-hidden="true" />
                Request Quote
              </Button>
            </div>

            <p className="mt-4 text-xs text-gray-500">
              Licensed Bay Area contractor since 1986.
            </p>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
